import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import {
  getProductsFromFirebase,
  deleteProductById,
  ProductItem,
} from "../../services/productService";
import { DocumentSnapshot } from "firebase/firestore";
import Loader from "../../Component/Loader";
import { ProductFormData } from "../../Forms/schemas/productFormSchema";

interface AdminProductTableProps {
  onEdit?: (product: ProductFormData & { id: string }) => void;
}

const AdminProductTable: React.FC<AdminProductTableProps> = ({ onEdit }) => {
  const [productList, setProductList] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [rowLoading, setRowLoading] = useState<string | null>(null);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    fetchInitialProducts();
  }, []);

  const fetchInitialProducts = async () => {
    try {
      setLoading(true);
      const { products, lastVisible } = await getProductsFromFirebase(6);
      setProductList(products);
      setLastVisible(lastVisible);
      if (products.length < 6) setHasMore(false);
    } catch (err) {
      toast.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextProducts = async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    try {
      const { products: nextProducts, lastVisible: newLastVisible } = await getProductsFromFirebase(
        6,
        lastVisible
      );
      setLastVisible(newLastVisible);
      if (nextProducts.length === 0) {
        setHasMore(false);
      } else {
        setProductList((prev) => [...prev, ...nextProducts]);
      }
    } catch (err) {
      toast.error("Failed to load more products.");
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleDelete = async (id: string) => {
    setRowLoading(id);
    try {
      await deleteProductById(id);
      toast.success("Product deleted");
      setProductList((prev) => prev.filter((product) => product.id !== id));
    } catch (err) {
      toast.error("Failed to delete product");
    } finally {
      setRowLoading(null);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Subtitle</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>CAS Number</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Loader />
              </TableCell>
            </TableRow>
          ) : productList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography align="center">No products available</Typography>
              </TableCell>
            </TableRow>
          ) : (
            productList.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  {product.base64Image ? (
                    <img
                      src={product.base64Image}
                      alt={product.name}
                      style={{ width: 80, height: 60, objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      No Image
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.subtitle}</TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>{product.casNumber}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() =>
                      onEdit &&
                      onEdit({
                        ...product,
                        id: product.id,
                      })
                    }
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(product.id)}
                    disabled={rowLoading === product.id}
                  >
                    {rowLoading === product.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="text-center my-4" style={{ textAlign: "center", margin: "1rem 0" }}>
          <Button variant="outlined" onClick={fetchNextProducts}>
            Load More
          </Button>
        </div>
      )}
    </TableContainer>
  );
};

export default AdminProductTable;
